/* eslint-disable no-console */
import React from 'react';
import PopupTypesEnum from '../Enums/PopupTypesEnum';
import WarningTypesEnum from '../Enums/WarningTypesEnum';
import FigmaPopup from './figmaPopup';
import LoadingFullScreen from './LoadingFullScreen';
import { addToastMessage, toastDurationMs, toastVariantClass, toastIcon, normalizeErrorMessage } from './toastHelpers';

// Module-level id source: monotonic so keys stay stable and never collide on
// rapid fires (unlike Date.now()).
let nextToastId = 1;

export default class ErrorMessage extends React.Component {
  static defaultProps = {};

  constructor(props) {
    super(props);
    ErrorMessage.SetErrorMessage = this.setErrorMessage.bind(this);
    ErrorMessage.SetNotification = this.setNotification.bind(this);
    ErrorMessage.SetSuccessMessage = this.setSuccessMessage.bind(this);
    ErrorMessage.SetWarning = this.setWarning.bind(this);
    ErrorMessage.SetSuccess = this.setSuccess.bind(this);
    ErrorMessage.SetSuccessWithWait = this.setSuccessWithWait.bind(this);
    ErrorMessage.SetApprove = this.setApprove.bind(this);
    ErrorMessage.SetInfo = this.setInfo.bind(this);
    ErrorMessage.SetLoading = this.setLoading.bind(this);
    ErrorMessage.SetIcon = this.setIcon.bind(this);
    ErrorMessage.SetWarningWithComment = this.setWarningWithComment.bind(this);

    // Per-toast auto-dismiss timers, keyed by toast id. Instance field (not
    // state) so cleanup is trivial.
    this.timers = new Map();

    this.state = {
      messages: [],
      popup: null,
    };
  }

  setMessage(message, type = 'error') {
    const text = normalizeErrorMessage(message, type);
    const id = nextToastId;
    nextToastId += 1;
    // MUST be the functional form. Toasts routinely arrive in pairs within one React batch —
    // a failed backend call raises the server's message from authService and then the caller's
    // own error code — and reading this.state.messages directly makes the second call compute
    // its list from a state the first has not been committed into yet, so the second silently
    // replaces the first. That is why a micro-down message could vanish while the error code
    // raised microseconds later survived.
    this.setState((prev) => {
      const next = addToastMessage(prev.messages, { id, type, message: text });
      return next === prev.messages ? null : { messages: next }; // null === deduped, no re-render
    });
    const ms = toastDurationMs(type);
    if (ms != null) {
      this.timers.set(
        id,
        window.setTimeout(() => this.remove(id), ms)
      );
    }
  }

  remove = (id) => {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    // A deduped toast still gets a timer (whether it was deduped is only known inside the
    // updater above), so bail rather than re-rendering for an id that was never added.
    this.setState((prev) =>
      prev.messages.some((m) => m.id === id) ? { messages: prev.messages.filter((m) => m.id !== id) } : null
    );
  };

  setErrorMessage = (message, error) => {
    this.setMessage(message, 'error');
    if (error !== undefined) console.error(error);
  };

  setNotification = (message) => {
    this.setMessage(message, 'notification');
  };

  setSuccessMessage = (message) => {
    this.setMessage(message, 'success');
  };

  setWarning = (message) => {
    this.setState({
      popup: (
        <FigmaPopup
          type={PopupTypesEnum.Warning}
          warningType={WarningTypesEnum.WithOneButton}
          text={message}
          okPopupFunction={() => this.setState({ popup: null })}
          closePopupFunction={() => this.setState({ popup: null })}
        />
      ),
    });
  };

  setWarningWithComment = (
    message,
    label,
    callbackFunction,
    okButtonText = 'Send',
    cancelButtonText = 'Cancel',
    showComment = true
  ) => {
    this.setState({
      popup: (
        <FigmaPopup
          type={PopupTypesEnum.Warning}
          warningType={showComment ? WarningTypesEnum.WithComment : WarningTypesEnum.WithTwoButtons}
          text={message}
          textForReason={label}
          overButtonText={label}
          okPopupFunction={(reason) => {
            callbackFunction(reason);
            this.setState({ popup: null });
          }}
          closePopupFunction={() => this.setState({ popup: null })}
          okButtonText={okButtonText}
          cancelButtonText={cancelButtonText}
        />
      ),
    });
  };

  setInfo = (message) => {
    this.setState({
      popup: (
        <FigmaPopup
          type={PopupTypesEnum.Info}
          text={message}
          okPopupFunction={() => this.setState({ popup: null })}
          closePopupFunction={() => this.setState({ popup: null })}
          showCopyToClipboard={true}
        />
      ),
    });
  };

  setSuccess = (message, okFunction) => {
    this.setState({
      popup: (
        <FigmaPopup
          type={PopupTypesEnum.Success}
          text={message}
          okPopupFunction={() => {
            this.setState({ popup: null });
            if (okFunction) okFunction();
          }}
          closePopupFunction={() => {
            this.setState({ popup: null });
            if (okFunction) okFunction();
          }}
        />
      ),
    });
  };

  setSuccessWithWait = (message) => {
    window.setTimeout(
      () =>
        this.setState({
          popup: (
            <FigmaPopup
              type={PopupTypesEnum.Success}
              text={message}
              okPopupFunction={() => this.setState({ popup: null })}
              closePopupFunction={() => this.setState({ popup: null })}
            />
          ),
        }),
      1000
    );
  };

  setApprove = (message, okFunction, overButtonText = 'DO YOU WANT TO CONTINUE?', cancelFunction = () => {}) => {
    this.setState({
      popup: (
        <FigmaPopup
          type={PopupTypesEnum.Attention}
          text={message}
          okPopupFunction={() => {
            this.setState({ popup: null });
            okFunction();
          }}
          closePopupFunction={() => {
            this.setState({ popup: null });
            cancelFunction();
          }}
          overButtonText={overButtonText}
        />
      ),
    });
  };

  setLoading = (loading) => {
    this.setState({
      popup: loading ? <LoadingFullScreen /> : null,
    });
  };

  // Legacy: no active callers remain (see NumberOfUnreadMessages/UndownloadedFiles
  // comments). Folded into the notification toast so the symbol still resolves.
  setIcon = (message) => {
    this.setMessage(message, 'notification');
  };

  componentWillUnmount() {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
  }

  render() {
    if (this.state.messages.length < 1 && this.state.popup === null) return null;

    return (
      <>
        {this.state.messages.length > 0 && (
          <div className='bravo-ds'>
            <div className='ds-toast-stack'>
              {this.state.messages.map((m) => (
                <div
                  className={`ds-toast ${toastVariantClass(m.type)}`}
                  key={m.id}
                  role={m.type === 'error' ? 'alert' : 'status'}
                >
                  <span className='ds-toast-ic'>
                    <i className={toastIcon(m.type)} />
                  </span>
                  <div className='ds-toast-msg'>{m.message}</div>
                  <button
                    type='button'
                    className='ds-toast-x'
                    aria-label='Dismiss'
                    onClick={() => this.remove(m.id)}
                  >
                    <i className='fa-solid fa-xmark' />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {this.state.popup}
      </>
    );
  }

  static SetErrorMessage = (message, error) => null;
  static SetNotification = (message) => null;
  static SetSuccessMessage = (message) => null;
  static SetWarning = (message) => null;
  static SetSuccess = (message, okFunction) => null;
  static SetSuccessWithWait = (message) => null;
  static SetInfo = (message) => null;
  static SetApprove = (message, okFunction, overButtonText, cancelFunction) => null;
  static SetLoading = (loading) => null;
  static SetIcon = (message) => null;
  static SetWarningWithComment = (
    message,
    label,
    callbackFunction,
    okButtonText,
    cancelButtonText,
    showComment = true
  ) => null;

  static LogDevInfo = (message, loc = '', envFunc = ErrorMessage.NotProd) => {
    if (!envFunc(message)) {
      return;
    }
    if (message == null) {
      console.log(message + (loc ? ` at: ${loc}` : ''));
      return;
    }
    if (typeof message === 'string') {
      console.log(message.toString() + (loc ? ` at: ${loc}` : ''));
      return;
    }

    console.log(`Object${loc ? ` at: ${loc}` : ''}`);
    console.log(message);
  };

  static IsDev = (message) =>
    process.env.REACT_APP_HOST_ENV === 'development' && ErrorMessage.NotFileForDownloadOrNotification(message);
  static IsStage = (message) =>
    (process.env.REACT_APP_HOST_ENV === 'staging' || process.env.REACT_APP_HOST_ENV === 'UAT') &&
    ErrorMessage.NotFileForDownloadOrNotification(message);
  static IsProd = (message) =>
    process.env.REACT_APP_HOST_ENV === 'production' && ErrorMessage.NotFileForDownloadOrNotification(message);
  static NotDev = (message) =>
    process.env.REACT_APP_HOST_ENV !== 'development' && ErrorMessage.NotFileForDownloadOrNotification(message);
  static NotStage = (message) =>
    process.env.REACT_APP_HOST_ENV !== 'staging' &&
    process.env.REACT_APP_HOST_ENV !== 'UAT' &&
    ErrorMessage.NotFileForDownloadOrNotification(message);
  static NotProd = (message) =>
    process.env.REACT_APP_HOST_ENV !== 'production' && ErrorMessage.NotFileForDownloadOrNotification(message);
  static EveryWhere = (message) => ErrorMessage.NotFileForDownloadOrNotification(message);

  static NotFileForDownloadOrNotification = (message) =>
    typeof message !== 'string' ||
    (typeof message === 'string' &&
      !message.includes('FileForDownload/GetNumberOfUndownloadedFiles') &&
      !message.includes('Notification/GetNumberOfUnreadMessages'));
}
